import RegisterDeletionPreview, { useRegisterDeletionPreview } from './RegisterDeletionPreview';
import { installRegisterSession } from '../utils/registerSession';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  InputAdornment,
  MenuItem,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
  BuildCircle as BuildCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  EditCalendar as EditCalendarIcon,
  Link as LinkIcon,
  MailOutline as MailOutlineIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { TRIAL_PRESETS, describeTrialFinalDay, filterTrialInvites, getTrialAttentionReasons, matchesTrialPreset } from './trialWorkbenchUtils';

const EMPTY_EXPIRY_DIALOG = {
  open: false,
  mode: 'edit',
  invite: null,
  expiryDate: '',
  submitting: null,
  error: '',
  result: null,
};

export function isValidDateOnly(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
}

export function isValidEmailAddress(value) {
  const email = String(value || '').trim().toLowerCase();
  return email.length > 3
    && email.length <= 320
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function buildTrialExpiryEmailConfirmation(invite, expectedExpiryDate) {
  return {
    expectedExpiryDate,
    expectedRecipientEmail: String(invite?.lifecycle_notification_email || '').trim().toLowerCase(),
  };
}

export function getTrialExpiryActionAvailability(invite) {
  if (!invite || invite.is_latest_for_database !== true) {
    return {
      canEdit: false,
      canSend: false,
      editReason: 'Only the current trial invite for this register can change the final trial date.',
      sendReason: 'Only the current trial invite for this register can send an expiry email.',
    };
  }

  const lifecycleStatus = invite.lifecycle_status;
  if (!lifecycleStatus) {
    const reason = 'Initialize trial email tracking before managing the final date or sending an expiry email.';
    return { canEdit: false, canSend: false, editReason: reason, sendReason: reason };
  }
  if (lifecycleStatus === 'converted') {
    const reason = 'Converted trials cannot change their trial date or send trial-expiry emails.';
    return { canEdit: false, canSend: false, editReason: reason, sendReason: reason };
  }
  if (lifecycleStatus === 'cancelled') {
    const reason = 'Cancelled trials cannot change their trial date or send trial-expiry emails.';
    return { canEdit: false, canSend: false, editReason: reason, sendReason: reason };
  }
  if (!['trialing', 'expired'].includes(lifecycleStatus)) {
    const reason = `Trial-expiry controls are unavailable for a ${lifecycleStatus} lifecycle.`;
    return { canEdit: false, canSend: false, editReason: reason, sendReason: reason };
  }

  if (!isValidEmailAddress(invite.lifecycle_notification_email)) {
    return {
      canEdit: true,
      canSend: false,
      editReason: null,
      sendReason: 'Set a valid trial notification recipient before sending an expiry email.',
    };
  }

  if (invite.lifecycle_latest_delivery?.requires_manual_reconciliation === true) {
    return {
      canEdit: true,
      canSend: false,
      editReason: null,
      sendReason: 'Reconcile the uncertain delivery in the email provider logs before sending another expiry email.',
    };
  }

  return {
    canEdit: true,
    canSend: true,
    editReason: null,
    sendReason: null,
  };
}

function formatDateTime(value) {
  return value ? dayjs(value).format('DD MMM YYYY, HH:mm') : 'N/A';
}

function buildContactName(row) {
  const fullName = [row.contact_first_name, row.contact_last_name].filter(Boolean).join(' ').trim();
  return fullName || 'N/A';
}

function getStatusTone(status) {
  switch (status) {
    case 'accepted':
      return {
        bg: 'rgba(18, 183, 106, 0.12)',
        color: '#067647',
        border: 'rgba(18, 183, 106, 0.22)',
      };
    case 'expired':
      return {
        bg: 'rgba(217, 45, 32, 0.10)',
        color: '#b42318',
        border: 'rgba(217, 45, 32, 0.18)',
      };
    default:
      return {
        bg: 'rgba(225, 179, 0, 0.12)',
        color: '#9a6700',
        border: 'rgba(225, 179, 0, 0.24)',
      };
  }
}

function getLifecycleTone(status) {
  switch (status) {
    case 'trialing':
      return {
        bg: 'rgba(41, 98, 255, 0.10)',
        color: '#1849a9',
        border: 'rgba(41, 98, 255, 0.18)',
      };
    case 'converted':
      return {
        bg: 'rgba(18, 183, 106, 0.12)',
        color: '#067647',
        border: 'rgba(18, 183, 106, 0.22)',
      };
    case 'expired':
      return {
        bg: 'rgba(217, 45, 32, 0.10)',
        color: '#b42318',
        border: 'rgba(217, 45, 32, 0.18)',
      };
    case 'cancelled':
      return {
        bg: 'rgba(71, 84, 103, 0.10)',
        color: '#475467',
        border: 'rgba(71, 84, 103, 0.18)',
      };
    default:
      return {
        bg: 'rgba(71, 84, 103, 0.08)',
        color: '#667085',
        border: 'rgba(71, 84, 103, 0.16)',
      };
  }
}

function getDeliveryEventLabel(delivery) {
  if (!delivery) return 'No delivery events';
  if (delivery.event_type === 'trial_expired') return 'Expired notice';
  if (delivery.event_type === 'trial_expiring') {
    return Number.isInteger(delivery.milestone_days)
      ? `${delivery.milestone_days}-day warning`
      : 'Expiry warning';
  }
  if (delivery.event_type === 'trial_expiry_manual') return 'Manual expiry notice';
  return String(delivery.event_type || 'Email event').replaceAll('_', ' ');
}

function getDeliveryHealthTone(delivery) {
  if (!delivery) {
    return {
      label: 'NO DELIVERY',
      bg: 'rgba(71, 84, 103, 0.08)',
      color: '#667085',
      border: 'rgba(71, 84, 103, 0.16)',
    };
  }
  if (delivery.requires_manual_reconciliation) {
    return {
      label: 'NEEDS REVIEW',
      bg: 'rgba(217, 45, 32, 0.10)',
      color: '#b42318',
      border: 'rgba(217, 45, 32, 0.22)',
    };
  }
  if (delivery.status === 'sent') {
    return {
      label: 'SENT',
      bg: 'rgba(18, 183, 106, 0.12)',
      color: '#067647',
      border: 'rgba(18, 183, 106, 0.22)',
    };
  }
  if (delivery.status === 'failed' && delivery.retry_exhausted) {
    return {
      label: 'RETRY EXHAUSTED',
      bg: 'rgba(217, 45, 32, 0.10)',
      color: '#b42318',
      border: 'rgba(217, 45, 32, 0.22)',
    };
  }
  if (delivery.status === 'failed') {
    return {
      label: delivery.next_attempt_at ? 'RETRY SCHEDULED' : 'FAILED',
      bg: 'rgba(225, 179, 0, 0.12)',
      color: '#9a6700',
      border: 'rgba(225, 179, 0, 0.24)',
    };
  }
  return {
    label: 'PROCESSING',
    bg: 'rgba(41, 98, 255, 0.10)',
    color: '#1849a9',
    border: 'rgba(41, 98, 255, 0.18)',
  };
}

export default function TrialRequests() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const query = params.get('q') || '';
  const status = TRIAL_PRESETS.some(preset => preset.value === params.get('status')) ? params.get('status') : 'all';
  const selectedTrialId = params.get('trial');
  const [loadError, setLoadError] = useState('');
  const [lastLoaded, setLastLoaded] = useState(null);
  const fetchSequence = useRef(0);
  const actionRequestInFlight = useRef(false);
  const updateQuery = updates => {
    const next = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && (key !== 'status' || value !== 'all')) next.set(key, value);
      else next.delete(key);
    });
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionBusyId, setActionBusyId] = useState(null);
  const [density, setDensity] = useState('standard');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  useEffect(() => { setPaginationModel(previous => ({ ...previous, page: 0 })); }, [query, status]);
  const [generatedLinkDialog, setGeneratedLinkDialog] = useState({
    open: false,
    inviteUrl: '',
    dbCode: '',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    invite: null,
    deleteDatabase: false,
    confirmationDatabaseName: '',
  });
  const deletionPreview = useRegisterDeletionPreview(deleteDialog.invite?.database_name, deleteDialog.open && deleteDialog.deleteDatabase);
  const [deletionError, setDeletionError] = useState('');
  const [conversionDialog, setConversionDialog] = useState({
    open: false,
    invite: null,
  });
  const [notificationDialog, setNotificationDialog] = useState({
    open: false,
    invite: null,
    enabled: false,
    email: '',
  });
  const [expiryDialog, setExpiryDialog] = useState(EMPTY_EXPIRY_DIALOG);
  const expirySubmitInFlight = useRef(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchInvites = useCallback(async ({ silent = false } = {}) => {
    const requestId = ++fetchSequence.current;
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadError('Your session is unavailable. Sign in again to load trials.');
      setLoading(false);
      return null;
    }
    if (!silent) setLoading(true);
    setLoadError('');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/trial-invites`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!Array.isArray(response.data)) throw new Error('Invalid trial invitation response');
      if (requestId !== fetchSequence.current) return null;
      setInvites(response.data);
      setLastLoaded(new Date());
      return response.data;
    } catch (error) {
      if (requestId === fetchSequence.current) setLoadError('Trial records could not be loaded. Retry to refresh the list; any visible records may be out of date.');
      return null;
    } finally {
      if (requestId === fetchSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvites();
    return () => { fetchSequence.current += 1; };
  }, [fetchInvites]);

  const summary = useMemo(() => {
    const pending = invites.filter((item) => item.status === 'pending').length;
    const accepted = invites.filter((item) => item.status === 'accepted').length;
    const expired = invites.filter((item) => item.status === 'expired').length;
    const manualReconciliation = invites.reduce((total, item) => {
      const delivery = item.lifecycle_latest_delivery;
      if (!delivery?.requires_manual_reconciliation) return total;
      return total + Math.max(1, Number(delivery.quarantined_delivery_count) || 0);
    }, 0);
    const retryExhausted = invites.filter(
      (item) => item.lifecycle_latest_delivery?.retry_exhausted === true
    ).length;

    return {
      total: invites.length,
      pending,
      accepted,
      expired,
      manualReconciliation,
      retryExhausted,
    };
  }, [invites]);

  const handleCopy = async (value, label) => {
    if (!value) {
      showToast(`No ${label.toLowerCase()} available.`, 'error');
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      showToast(`${label} copied.`);
    } catch (error) {
      showToast(`Failed to copy ${label.toLowerCase()}.`, 'error');
    }
  };

  const handleGenerateLink = async (id) => {
    if (actionRequestInFlight.current) return;
    actionRequestInFlight.current = true;
    try {
      setActionBusyId(id);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/trial-invites/${id}/generate-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const inviteUrl = response.data.inviteUrl || '';
      try {
        await navigator.clipboard.writeText(inviteUrl);
        showToast('Fresh invite link copied.');
      } catch (copyError) {
        setGeneratedLinkDialog({
          open: true,
          inviteUrl,
          dbCode: response.data.dbCode || '',
        });
        showToast('Fresh invite link generated. Copy it from the dialog.');
      }
      await fetchInvites({ silent: true });
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to generate invite link.', 'error');
    } finally {
      actionRequestInFlight.current = false;
      setActionBusyId(null);
    }
  };

  const handleResend = async (id) => {
    if (actionRequestInFlight.current) return;
    actionRequestInFlight.current = true;
    try {
      setActionBusyId(id);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/trial-invites/${id}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.emailSent === false) {
        setGeneratedLinkDialog({
          open: true,
          inviteUrl: response.data?.inviteUrl || '',
          dbCode: response.data?.dbCode || '',
        });
        const deliveryError = response.data?.emailError
          ? ` Email error: ${String(response.data.emailError)}`
          : '';
        showToast(
          `${response.data?.message || 'The trial invitation email could not be sent.'}${deliveryError} The fresh link remains available in the open dialog for manual copying.`,
          'warning'
        );
      } else {
        showToast(response.data?.message || 'Trial invitation resent.');
      }
      await fetchInvites({ silent: true });
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to resend trial invitation.', 'error');
    } finally {
      actionRequestInFlight.current = false;
      setActionBusyId(null);
    }
  };

  const handleOpenConversionDialog = (invite) => {
    setConversionDialog({ open: true, invite });
  };

  const handleCloseConversionDialog = () => {
    setConversionDialog({ open: false, invite: null });
  };

  const handleConvertTrial = async () => {
    const invite = conversionDialog.invite;
    if (!invite) return;

    if (actionRequestInFlight.current) return;
    actionRequestInFlight.current = true;
    try {
      setActionBusyId(invite.id);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/trial-invites/${invite.id}/convert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(response.data?.message || 'Trial marked as converted. Expiry emails have been stopped.');
      handleCloseConversionDialog();
      await fetchInvites({ silent: true });
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to mark the trial as converted.', 'error');
    } finally {
      actionRequestInFlight.current = false;
      setActionBusyId(null);
    }
  };

  const handleOpenNotificationDialog = (invite, enabled) => {
    setNotificationDialog({ open: true, invite, enabled, email: invite.lifecycle_notification_email || invite.email || '' });
  };

  const handleCloseNotificationDialog = () => {
    setNotificationDialog({ open: false, invite: null, enabled: false, email: '' });
  };

  const handleUpdateTrialNotifications = async () => {
    const { invite, enabled, email } = notificationDialog;
    if (!invite || !['trialing', 'expired'].includes(invite.lifecycle_status)) return;
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (enabled && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      showToast('Enter a valid notification recipient email.', 'error');
      return;
    }

    if (actionRequestInFlight.current) return;
    actionRequestInFlight.current = true;
    try {
      setActionBusyId(invite.id);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/trial-invites/${invite.id}/notifications`,
        { enabled, notificationEmail: enabled ? normalizedEmail : undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(
        response.data?.message ||
          (enabled
            ? 'Trial-expiry emails enabled.'
            : 'Trial-expiry emails disabled. Trial access was not changed.')
      );
      handleCloseNotificationDialog();
      await fetchInvites({ silent: true });
    } catch (error) {
      showToast(
        error.response?.data?.message ||
          `Failed to ${enabled ? 'enable' : 'disable'} trial-expiry emails.`,
        'error'
      );
    } finally {
      actionRequestInFlight.current = false;
      setActionBusyId(null);
    }
  };

  const handleInitializeTracking = async (invite) => {
    if (!invite || invite.lifecycle_status) return;
    if (actionRequestInFlight.current) return;
    actionRequestInFlight.current = true;
    try {
      setActionBusyId(invite.id);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/trial-invites/${invite.id}/tracking`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(response.data?.message || 'Trial email tracking initialized.');
      await fetchInvites({ silent: true });
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to initialize trial email tracking.', 'error');
    } finally {
      actionRequestInFlight.current = false;
      setActionBusyId(null);
    }
  };

  const handleOpenExpiryDialog = (invite, mode) => {
    const availability = getTrialExpiryActionAvailability(invite);
    const allowed = mode === 'send' ? availability.canSend : availability.canEdit;
    const unavailableReason = mode === 'send'
      ? availability.sendReason
      : availability.editReason;

    if (!allowed) {
      showToast(unavailableReason || 'This trial action is unavailable.', 'error');
      return;
    }

    setExpiryDialog({
      ...EMPTY_EXPIRY_DIALOG,
      open: true,
      mode,
      invite,
      expiryDate: invite.expiry_date || '',
    });
  };

  const handleCloseExpiryDialog = () => {
    if (expirySubmitInFlight.current) return;
    setExpiryDialog(EMPTY_EXPIRY_DIALOG);
  };

  const handleExpiryDialogSubmit = async ({ saveDate, sendEmail }) => {
    if (expirySubmitInFlight.current) return;

    const { invite, expiryDate } = expiryDialog;
    if (!invite) return;

    const availability = getTrialExpiryActionAvailability(invite);
    if (saveDate && !availability.canEdit) {
      setExpiryDialog((previous) => ({
        ...previous,
        error: availability.editReason || 'This trial date can no longer be changed.',
      }));
      return;
    }
    if (sendEmail && !availability.canSend) {
      setExpiryDialog((previous) => ({
        ...previous,
        error: availability.sendReason || 'An expiry email cannot be sent for this trial.',
      }));
      return;
    }
    if (saveDate && !isValidDateOnly(expiryDate)) {
      setExpiryDialog((previous) => ({
        ...previous,
        error: 'Enter a real calendar date in YYYY-MM-DD format.',
      }));
      return;
    }
    if (saveDate && expiryDate === invite.expiry_date) {
      setExpiryDialog((previous) => ({
        ...previous,
        error: 'Choose a different final trial date before saving.',
      }));
      return;
    }

    const submitting = saveDate && sendEmail
      ? 'save-and-send'
      : saveDate
        ? 'save'
        : 'send';
    const token = localStorage.getItem('token');
    let dateSaved = false;
    let savedExpiryDate = expiryDate;
    let updateMessage = '';

    expirySubmitInFlight.current = true;
    setExpiryDialog((previous) => ({
      ...previous,
      submitting,
      error: '',
      result: null,
    }));

    try {
      if (saveDate) {
        const updateResponse = await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/trial-invites/${invite.id}/expiry`,
          { expiryDate },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        dateSaved = true;
        savedExpiryDate = updateResponse.data?.expiryDate || expiryDate;
        updateMessage = updateResponse.data?.message || `Final trial date saved as ${savedExpiryDate}.`;

        if (!sendEmail) {
          setExpiryDialog((previous) => ({
            ...previous,
            expiryDate: savedExpiryDate,
            submitting: null,
            result: {
              severity: 'success',
              title: 'Final trial date saved',
              message: updateMessage,
              expiryDate: savedExpiryDate,
              reactivated: updateResponse.data?.reactivated === true,
            },
          }));
          showToast(updateMessage);
          await fetchInvites({ silent: true });
          return;
        }
      }

      const emailResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/trial-invites/${invite.id}/expiry-email`,
        buildTrialExpiryEmailConfirmation(invite, savedExpiryDate),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const emailResult = emailResponse.data || {};
      const requiresManualReconciliation = emailResponse.status === 202
        || emailResult.requiresManualReconciliation === true
        || emailResult.deliveryStatus === 'processing';
      const emailLabel = emailResult.emailKind === 'ended'
        ? 'Trial-ended email'
        : 'Trial reminder email';
      const resultMessage = [
        updateMessage,
        emailResult.message || `${emailLabel} sent.`,
      ].filter(Boolean).join(' ');

      setExpiryDialog((previous) => ({
        ...previous,
        expiryDate: emailResult.expiryDate || savedExpiryDate,
        submitting: null,
        result: {
          severity: requiresManualReconciliation ? 'warning' : 'success',
          title: requiresManualReconciliation
            ? 'Email accepted; verify delivery'
            : `${emailLabel} sent`,
          message: resultMessage,
          emailKind: emailResult.emailKind,
          recipientEmail: emailResult.recipientEmail,
          expiryDate: emailResult.expiryDate || savedExpiryDate,
          deliveryStatus: emailResult.deliveryStatus,
          requiresManualReconciliation,
        },
      }));
      showToast(
        resultMessage,
        requiresManualReconciliation ? 'warning' : 'success'
      );
      await fetchInvites({ silent: true });
    } catch (error) {
      const errorData = error.response?.data || {};
      const apiMessage = errorData.message
        || (sendEmail ? 'Failed to send the trial-expiry email.' : 'Failed to update the final trial date.');

      if (errorData.code === 'TRIAL_EMAIL_CONFIRMATION_STALE') {
        const refreshedInvites = await fetchInvites({ silent: true });
        const refreshedInvite = refreshedInvites?.find(({ id }) => id === invite.id) || invite;
        const staleMessage = dateSaved
          ? `The final trial date ${savedExpiryDate} was saved, but no email was sent because the recipient or date changed before delivery. Review the refreshed details and confirm again.`
          : apiMessage;
        setExpiryDialog((previous) => ({
          ...previous,
          invite: refreshedInvite,
          expiryDate: refreshedInvite.expiry_date || '',
          submitting: null,
          error: '',
          result: {
            severity: 'warning',
            title: 'Details changed; email not sent',
            message: staleMessage,
            expiryDate: refreshedInvite.expiry_date || null,
          },
        }));
        showToast(staleMessage, 'warning');
      } else if (errorData.authoritativeExpiryUpdated === true) {
        const authoritativeExpiryDate = errorData.expiryDate || expiryDate;
        const partialMessage = `${apiMessage} The workspace now uses ${authoritativeExpiryDate} as its inclusive final day, but the Trial Requests tracking record may still show the previous date.`;
        setExpiryDialog((previous) => ({
          ...previous,
          expiryDate: authoritativeExpiryDate,
          submitting: null,
          error: '',
          result: {
            severity: 'warning',
            title: 'Workspace date updated; tracking needs attention',
            message: partialMessage,
            expiryDate: authoritativeExpiryDate,
          },
        }));
        showToast(partialMessage, 'warning');
        await fetchInvites({ silent: true });
      } else if (dateSaved) {
        const partialMessage = `The final trial date ${savedExpiryDate} was saved and remains in effect, but the email was not sent. ${apiMessage}`;
        setExpiryDialog((previous) => ({
          ...previous,
          expiryDate: savedExpiryDate,
          submitting: null,
          error: '',
          result: {
            severity: 'warning',
            title: 'Date saved; email not sent',
            message: partialMessage,
            expiryDate: savedExpiryDate,
          },
        }));
        showToast(partialMessage, 'warning');
        await fetchInvites({ silent: true });
      } else if (
        sendEmail
        && ['TRIAL_EMAIL_ALREADY_CLAIMED', 'TRIAL_EMAIL_RECONCILIATION_REQUIRED'].includes(errorData.code)
      ) {
        const requiresManualReconciliation = errorData.code === 'TRIAL_EMAIL_RECONCILIATION_REQUIRED';
        setExpiryDialog((previous) => ({
          ...previous,
          submitting: null,
          error: '',
          result: {
            severity: 'warning',
            title: requiresManualReconciliation
              ? 'Resolve the uncertain delivery first'
              : 'No additional email was sent',
            message: apiMessage,
            deliveryStatus: errorData.deliveryStatus,
            requiresManualReconciliation,
          },
        }));
        showToast(apiMessage, 'warning');
        await fetchInvites({ silent: true });
      } else {
        setExpiryDialog((previous) => ({
          ...previous,
          submitting: null,
          error: apiMessage,
        }));
        showToast(apiMessage, 'error');
      }
    } finally {
      expirySubmitInFlight.current = false;
      setExpiryDialog((previous) => ({ ...previous, submitting: null }));
    }
  };

  const handleOpenDeleteDialog = (invite) => {
    setDeletionError('');
    setDeleteDialog({
      open: true,
      invite,
      deleteDatabase: false,
      confirmationDatabaseName: '',
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      invite: null,
      deleteDatabase: false,
      confirmationDatabaseName: '',
    });
  };

  const handleDeleteInvite = async () => {
    const invite = deleteDialog.invite;
    if (!invite || (deleteDialog.deleteDatabase && !deletionPreview.preview)) return;

    if (
      deleteDialog.deleteDatabase &&
      deleteDialog.confirmationDatabaseName.trim() !== invite.database_name
    ) {
      showToast(`Type '${invite.database_name}' to confirm deleting the database.`, 'error');
      return;
    }

    if (actionRequestInFlight.current) return;
    actionRequestInFlight.current = true;
    try {
      setActionBusyId(invite.id);
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/trial-invites/${invite.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            deleteDatabase: deleteDialog.deleteDatabase,
            confirmationDatabaseName: deleteDialog.confirmationDatabaseName,
            deletionToken: deleteDialog.deleteDatabase ? deletionPreview.preview.deletionToken : undefined,
          },
        }
      );
      if (response.data?.token) { installRegisterSession(response.data.token); return; }
      showToast(response.data?.message || 'Trial invite deleted.');
      updateQuery({ trial: '' });
      handleCloseDeleteDialog();
      await fetchInvites({ silent: true });
    } catch (error) {
      if (error.response?.data?.token) { installRegisterSession(error.response.data.token); return; }
      setDeletionError(error.response?.data?.message || 'Failed to delete trial invite.');
      if (error.response?.status === 409 || error.response?.data?.code === 'REGISTER_DELETE_INCOMPLETE') deletionPreview.reload();
    } finally {
      actionRequestInFlight.current = false;
      setActionBusyId(null);
    }
  };

  const visibleInvites = useMemo(() => filterTrialInvites(invites, { status, query }), [invites, status, query]);
  const selectedTrial = invites.find(invite => String(invite.id) === selectedTrialId) || null;
  const presetCounts = Object.fromEntries(TRIAL_PRESETS.map(preset => [preset.value, invites.filter(invite => matchesTrialPreset(invite, preset.value)).length]));
  const openTrial = invite => updateQuery({ trial: String(invite.id) });
  const closeTrial = () => updateQuery({ trial: '' });
  const stateBadge = (value, lifecycle = false, invite = null) => {
    if (lifecycle && ['trialing', 'expired'].includes(value) && Number.isFinite(invite?.daysUntilExpiry)) value = invite.daysUntilExpiry < 0 ? 'expired' : 'trialing';
    const tone = lifecycle ? getLifecycleTone(value) : getStatusTone(value);
    const labels = lifecycle ? { trialing: 'In trial', converted: 'Converted', expired: 'Expired', cancelled: 'Cancelled' } : { pending: 'Awaiting signup', accepted: 'Signed up', expired: 'Invite expired' };
    return <Chip size="small" label={labels[value] || (lifecycle ? 'Untracked' : value || 'Unknown')} sx={{ height: 23, fontSize: 12, fontWeight: 600, bgcolor: tone.bg, color: tone.color, border: `1px solid ${tone.border}` }} />;
  };
  const detailField = (label, value) => <Box key={label} sx={{ minWidth: 0 }}><Typography component="dt" variant="caption" color="text.secondary">{label}</Typography><Typography component="dd" sx={{ m: 0, fontSize: 14, overflowWrap: 'anywhere', fontWeight: 500 }}>{value || 'Not recorded'}</Typography></Box>;
  const renderTrialActions = invite => {
    const lifecycleCanManage = ['trialing', 'expired'].includes(invite.lifecycle_status);
    const canInvite = invite.status === 'pending' && lifecycleCanManage;
    const expiry = getTrialExpiryActionAvailability(invite);
    const inviteReason = invite.status !== 'pending' ? 'Only pending invitations can receive a fresh access link.' : !lifecycleCanManage ? 'An active or expired tracked lifecycle is required.' : '';
    const action = (label, icon, enabled, reason, onClick, color = 'primary') => <Tooltip key={label} title={!enabled ? reason || 'This action is unavailable for this trial.' : ''}><span style={{ display: 'inline-flex' }}><Button size="small" variant="outlined" startIcon={icon} color={color} onClick={onClick} disabled={!enabled || loading || Boolean(loadError) || actionBusyId != null || expiryDialogBusy}>{label}</Button></span></Tooltip>;
    return <Stack spacing={2}>
      <Box><Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Invitation & access</Typography><Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
        {action('Copy fresh invite link', <LinkIcon />, canInvite, inviteReason, () => handleGenerateLink(invite.id))}
        {action('Resend onboarding email', <SendIcon />, canInvite, inviteReason, () => handleResend(invite.id))}
        {action('Copy register code', <ContentCopyIcon />, !!invite.db_code, 'No register code is recorded.', () => handleCopy(invite.db_code, 'Register code'))}
      </Stack><Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>Generating or resending a fresh link replaces the previous invitation link.</Typography></Box>
      <Box><Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Trial date & reminders</Typography><Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
        {action('Edit final trial date', <EditCalendarIcon />, expiry.canEdit, expiry.editReason, () => handleOpenExpiryDialog(invite, 'edit'))}
        {action('Send trial-expiry email now', <MailOutlineIcon />, expiry.canSend, expiry.sendReason, () => handleOpenExpiryDialog(invite, 'send'))}
        {action(invite.lifecycle_notifications_enabled ? 'Disable trial-expiry emails' : 'Enable trial-expiry emails', invite.lifecycle_notifications_enabled ? <NotificationsOffIcon /> : <NotificationsActiveIcon />, lifecycleCanManage, 'Only an active or expired tracked trial can change reminder settings.', () => handleOpenNotificationDialog(invite, !invite.lifecycle_notifications_enabled))}
        {!invite.lifecycle_status && action('Initialize trial email tracking', <BuildCircleIcon />, invite.can_initialize_tracking === true, 'Only the latest invite can initialize tracking.', () => handleInitializeTracking(invite))}
      </Stack>{!expiry.canSend && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{expiry.sendReason}</Typography>}</Box>
      <Box><Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Outcome & cleanup</Typography><Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
        {action('Mark trial as converted', <CheckCircleOutlineIcon />, lifecycleCanManage, 'Conversion requires an active or expired tracked trial.', () => handleOpenConversionDialog(invite), 'success')}
        {action('Delete trial invite', <DeleteIcon />, true, '', () => handleOpenDeleteDialog(invite), 'error')}
      </Stack><Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>Conversion stops trial emails. Extend access and update the plan separately in register settings.</Typography></Box>
    </Stack>;
  };
  const CustomToolbar = () => <GridToolbarContainer sx={{ px: 1, py: 0.5, gap: 0.5 }}><GridToolbarColumnsButton /><GridToolbarFilterButton /><GridToolbarDensitySelector /><GridToolbarExport /><Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', mr: 1 }}>{visibleInvites.length} of {invites.length} invitations</Typography></GridToolbarContainer>;
  const columns = [
    { field: 'company_name', headerName: 'Company / contact', minWidth: 210, flex: 1.3, renderCell: ({ row }) => <Box sx={{ minWidth: 0, width: '100%' }}><Button variant="text" size="small" onClick={() => openTrial(row)} sx={{ p: 0, display: 'block', justifyContent: 'flex-start', textAlign: 'left', minWidth: 0, maxWidth: '100%', fontSize: 14, fontWeight: 600, '&&&': { minHeight: 0, lineHeight: 1.3 } }}><Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>{row.company_name || row.email || 'Unnamed trial'}</Typography></Button><Typography noWrap variant="caption" color="text.secondary" sx={{ display: 'block' }}>{row.email || buildContactName(row)}</Typography></Box> },
    { field: 'database_name', headerName: 'Register / plan', minWidth: 180, flex: 1, renderCell: ({ row }) => <Box sx={{ minWidth: 0, width: '100%' }}><Typography title={row.database_name || ''} noWrap variant="body2">{row.database_name || 'Not recorded'}</Typography><Typography noWrap variant="caption" color="text.secondary" sx={{ display: 'block' }}>{row.plan_type || 'Plan unavailable'}{row.is_latest_for_database === false ? ' · Previous invite' : ''}</Typography></Box> },
    { field: 'status', headerName: 'Invitation', width: 145, renderCell: ({ value }) => stateBadge(value) },
    { field: 'lifecycle_status', headerName: 'Trial lifecycle', width: 115, renderCell: ({ row, value }) => stateBadge(value, true, row) },
    { field: 'expiry_date', headerName: 'Final trial day', width: 145, description: 'Inclusive final day; remaining days use the server business calendar.', renderCell: ({ row }) => <Box><Typography sx={{ fontSize: 14 }}>{row.expiry_date ? dayjs(row.expiry_date).format('DD MMM YYYY') : 'Not recorded'}</Typography><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{describeTrialFinalDay(row)}</Typography></Box> },
    { field: 'attention', headerName: 'Follow-up', minWidth: 180, flex: 1, valueGetter: (_value, row) => getTrialAttentionReasons(row).join('; '), renderCell: ({ row }) => { const reasons = getTrialAttentionReasons(row); return reasons.length ? <Tooltip title={reasons.join(' · ')}><Button size="small" color="warning" onClick={() => openTrial(row)} sx={{ p: 0, fontSize: 12, justifyContent: 'flex-start', textAlign: 'left' }}>{reasons[0]}{reasons.length > 1 ? ` +${reasons.length - 1}` : ''}</Button></Tooltip> : <Typography variant="caption" color="text.secondary">{row.lifecycle_notifications_enabled ? 'Reminders enabled' : ['converted', 'cancelled'].includes(row.lifecycle_status) ? 'Reminders stopped' : 'Reminders off'}</Typography>; } },
    { field: 'actions', headerName: 'Actions', width: 96, sortable: false, filterable: false, disableExport: true, hideable: false, pinnable: false, resizable: false, renderCell: ({ row }) => <Button size="small" variant="outlined" onClick={() => openTrial(row)} aria-label={`Open trial ${row.company_name || row.database_name}`}>Open</Button> },
    { field: 'email', headerName: 'Contact email', width: 230 },
    { field: 'contact_name', headerName: 'Contact name', width: 170, valueGetter: (_value, row) => buildContactName(row) },
    { field: 'db_code', headerName: 'Register code', width: 150 },
    { field: 'plan_type', headerName: 'Plan', width: 120 },
    { field: 'sent_at', headerName: 'Last onboarding email', width: 190, valueFormatter: value => formatDateTime(value) },
    { field: 'accepted_at', headerName: 'Signup date', width: 180, valueFormatter: value => formatDateTime(value) },
  ];
  const EmptyTrialsOverlay = () => <Stack height="100%" alignItems="center" justifyContent="center" spacing={1} sx={{ p: 3, textAlign: 'center' }}><Typography sx={{ fontWeight: 600 }}>{loadError ? 'Trial data is unavailable' : invites.length ? 'No invitations match these filters' : 'No trial invitations yet'}</Typography><Typography variant="body2" color="text.secondary">{loadError ? 'Retry the request above.' : invites.length ? 'Change the status or clear the search to see more records.' : 'Create a trial using a new or existing register.'}</Typography></Stack>;

  const expiryDialogAvailability = getTrialExpiryActionAvailability(expiryDialog.invite);
  const expiryDialogBusy = Boolean(expiryDialog.submitting);
  const expiryDateValid = isValidDateOnly(expiryDialog.expiryDate);
  const expiryDateChanged = expiryDialog.expiryDate !== (expiryDialog.invite?.expiry_date || '');
  const expiryRecipient = expiryDialog.invite?.lifecycle_notification_email || 'N/A';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 1.5, width: '100%', minWidth: 0, minHeight: 520 }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" useFlexGap flexWrap="wrap" gap={1}>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchInvites()} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</Button><Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/databases?openTrial=1')}>Create trial</Button>
      </Stack>
      <Paper className="admin-table-card" variant="outlined" sx={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 480 }}>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.5} sx={{ px: 1, pt: 1 }} aria-label="Trial filters">
          {TRIAL_PRESETS.slice(0, 8).map(preset => <Button key={preset.value} size="small" variant={status === preset.value ? 'contained' : 'text'} onClick={() => updateQuery({ status: preset.value })} aria-pressed={status === preset.value} sx={{ fontSize: 12, px: 1 }}>{preset.label}<Box component="span" sx={{ ml: 0.75, fontWeight: 600 }}>{lastLoaded ? presetCounts[preset.value] : '—'}</Box></Button>)}
        </Stack>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField size="small" label="Search trials" placeholder="Company, contact, register, or code" value={query} onChange={event => updateQuery({ q: event.target.value })} sx={{ flex: '1 1 260px' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="View" value={status} onChange={event => updateQuery({ status: event.target.value })} sx={{ minWidth: 205 }}>{TRIAL_PRESETS.map(preset => <MenuItem key={preset.value} value={preset.value}>{preset.label}</MenuItem>)}</TextField>
          {(query || status !== 'all') && <Button size="small" onClick={() => updateQuery({ q: '', status: 'all' })}>Clear filters</Button>}
        </Stack>
        {loadError && <Alert severity="error" action={<Button color="inherit" size="small" disabled={loading} onClick={() => fetchInvites()}>Retry</Button>}>{loadError}</Alert>}
        {summary.manualReconciliation > 0 && <Alert severity="error" action={<Button size="small" color="inherit" onClick={() => updateQuery({ status: 'attention' })}>Review</Button>}>{summary.manualReconciliation} unresolved email claim{summary.manualReconciliation === 1 ? '' : 's'}. Delivery is uncertain; check provider logs before another manual send.</Alert>}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 360, display: 'flex' }}><DataGridPro rows={visibleInvites} columns={columns} getRowId={row => row.id} getRowHeight={() => 'auto'} getEstimatedRowHeight={() => 80} density={density} loading={loading} pagination paginationModel={paginationModel} onPaginationModelChange={setPaginationModel} disableRowSelectionOnClick pageSizeOptions={[25, 50, 100]} initialState={{ pinnedColumns: { right: ['actions'] }, columns: { columnVisibilityModel: { email: false, contact_name: false, db_code: false, plan_type: false, sent_at: false, accepted_at: false } } }} onDensityChange={setDensity} slots={{ toolbar: CustomToolbar, noRowsOverlay: EmptyTrialsOverlay, noResultsOverlay: EmptyTrialsOverlay }} sx={{ flex: 1, minWidth: 0, border: 0, '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', lineHeight: 1.5, py: density === 'compact' ? 1 : density === 'comfortable' ? 2.5 : 2, borderBottom: '1px solid #dbe2ea' }, '& .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-columnHeader--pinnedRight': { borderLeft: '1px solid #dbe2ea' } }} /></Box>
        <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>{lastLoaded ? `Refreshed ${dayjs(lastLoaded).format('HH:mm:ss')}` : 'Loading trial records…'} · Counts are invitation records; earlier invitations remain available.</Typography>
      </Paper>
      <Drawer sx={{ zIndex: theme => theme.zIndex.drawer + 2 }} anchor="right" open={Boolean(selectedTrialId)} onClose={closeTrial} PaperProps={{ role: 'dialog', 'aria-label': 'Trial details', sx: { width: { xs: '100%', sm: 590 }, maxWidth: '100vw' } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}><Box><Typography sx={{ fontSize: 18, fontWeight: 600 }}>{selectedTrial?.company_name || 'Trial details'}</Typography><Typography variant="body2" color="text.secondary">{selectedTrial?.database_name || `Invitation ${selectedTrialId}`}</Typography></Box><IconButton size="small" aria-label="Close trial details" onClick={closeTrial}><CloseIcon /></IconButton></Stack>
        {selectedTrial ? <Stack spacing={2} sx={{ p: 2 }}>
          {loadError && <Alert severity="warning">These records may be out of date. Refresh the trial list before taking an action.</Alert>}
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>{stateBadge(selectedTrial.status)}{stateBadge(selectedTrial.lifecycle_status, true, selectedTrial)}{selectedTrial.is_latest_for_database === false && <Chip size="small" variant="outlined" label="Previous invitation" />}</Stack>
          {!!getTrialAttentionReasons(selectedTrial).length && <Alert severity={selectedTrial.lifecycle_latest_delivery?.requires_manual_reconciliation ? 'error' : 'warning'}><AlertTitle>Follow-up needed</AlertTitle>{getTrialAttentionReasons(selectedTrial).map(reason => <Typography variant="body2" key={reason}>{reason}</Typography>)}{selectedTrial.lifecycle_latest_delivery?.requires_manual_reconciliation && <Typography variant="body2" sx={{ mt: 1 }}>Do not retry blindly. Verify the delivery in provider logs; EXREG has quarantined the uncertain claim.</Typography>}</Alert>}
          <Box><Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Contact & register</Typography><Box component="dl" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, m: 0 }}>{detailField('Contact', buildContactName(selectedTrial))}{detailField('Contact email', selectedTrial.email)}{detailField('Register', selectedTrial.database_name)}{detailField('Register code', selectedTrial.db_code)}{detailField('Plan', selectedTrial.plan_type)}{detailField('Final trial day (inclusive)', selectedTrial.expiry_date ? `${dayjs(selectedTrial.expiry_date).format('DD MMM YYYY')}${describeTrialFinalDay(selectedTrial) ? ` · ${describeTrialFinalDay(selectedTrial)}` : ''}` : '')}{detailField('Signed-up user ID', selectedTrial.accepted_user_id)}{detailField('Signed up at', selectedTrial.accepted_at ? formatDateTime(selectedTrial.accepted_at) : '')}</Box><Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}><Button size="small" startIcon={<ContentCopyIcon />} disabled={!selectedTrial.email} onClick={() => handleCopy(selectedTrial.email, 'Contact email')}>Copy email</Button><Button size="small" startIcon={<OpenInNewIcon />} onClick={() => navigate(`/admin/databases?q=${encodeURIComponent(selectedTrial.database_name || '')}`)}>Register settings</Button><Button size="small" startIcon={<OpenInNewIcon />} onClick={() => navigate(`/admin/users?register=${encodeURIComponent(selectedTrial.database_name || '')}`)}>Register users</Button></Stack></Box>
          <Divider />
          {renderTrialActions(selectedTrial)}
          <Divider />
          <Box><Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Email delivery & tracking</Typography><Box component="dl" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, m: 0 }}>{detailField('Automatic expiry reminders', selectedTrial.lifecycle_status ? ['converted', 'cancelled'].includes(selectedTrial.lifecycle_status) ? 'Stopped' : selectedTrial.lifecycle_notifications_enabled ? 'Enabled' : 'Disabled' : 'Untracked')}{detailField('Notification recipient', selectedTrial.lifecycle_notification_email)}{detailField('Onboarding email last sent', selectedTrial.sent_at ? formatDateTime(selectedTrial.sent_at) : '')}{detailField('Onboarding email last attempted', selectedTrial.email_attempted_at ? formatDateTime(selectedTrial.email_attempted_at) : '')}</Box>{selectedTrial.email_last_error && <Alert severity="warning" sx={{ mt: 1 }}>{selectedTrial.email_last_error}</Alert>}
          {selectedTrial.lifecycle_latest_delivery ? <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}><Typography sx={{ fontSize: 14, fontWeight: 600 }}>{getDeliveryEventLabel(selectedTrial.lifecycle_latest_delivery)} · {getDeliveryHealthTone(selectedTrial.lifecycle_latest_delivery).label}</Typography><Box component="dl" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1, mb: 0 }}>{detailField('Delivery recipient', selectedTrial.lifecycle_latest_delivery.recipient_email)}{detailField('Attempts', String(selectedTrial.lifecycle_latest_delivery.attempts || 0))}{detailField('Sent at', selectedTrial.lifecycle_latest_delivery.sent_at ? formatDateTime(selectedTrial.lifecycle_latest_delivery.sent_at) : '')}{detailField('Next retry', selectedTrial.lifecycle_latest_delivery.next_attempt_at ? formatDateTime(selectedTrial.lifecycle_latest_delivery.next_attempt_at) : '')}{selectedTrial.lifecycle_latest_delivery.requires_manual_reconciliation && detailField('Unresolved claim IDs', (selectedTrial.lifecycle_latest_delivery.quarantined_delivery_ids || []).join(', '))}{selectedTrial.lifecycle_latest_delivery.requires_manual_reconciliation && detailField('Oldest unresolved claim', formatDateTime(selectedTrial.lifecycle_latest_delivery.oldest_quarantined_claimed_at))}</Box>{selectedTrial.lifecycle_latest_delivery.last_error && <Typography variant="body2" color="error.main" sx={{ mt: 1, overflowWrap: 'anywhere' }}>{selectedTrial.lifecycle_latest_delivery.last_error}</Typography>}</Box> : <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No trial-expiry email delivery is recorded.</Typography>}</Box>
          <Divider />
          <Box><Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Record history</Typography><Box component="dl" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, m: 0 }}>{detailField('Invitation ID', String(selectedTrial.id))}{detailField('Created by', selectedTrial.created_by_username)}{detailField('Created', selectedTrial.created_at ? formatDateTime(selectedTrial.created_at) : '')}{detailField('Converted', selectedTrial.lifecycle_converted_at ? formatDateTime(selectedTrial.lifecycle_converted_at) : '')}</Box></Box>
        </Stack> : <Alert severity={loading ? 'info' : 'warning'} sx={{ m: 2 }}>{loading ? 'Loading the selected trial…' : loadError ? 'The selected trial could not be loaded. Close this panel and retry.' : 'This invitation is no longer available. Close this panel and choose another record.'}</Alert>}
      </Drawer>

      <Dialog
        open={generatedLinkDialog.open}
        onClose={() => setGeneratedLinkDialog({ open: false, inviteUrl: '', dbCode: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Fresh Invite Link</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Invite Link"
            value={generatedLinkDialog.inviteUrl}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
          />
          {generatedLinkDialog.dbCode && (
            <TextField
              label="Register Code"
              value={generatedLinkDialog.dbCode}
              fullWidth
              margin="normal"
              InputProps={{ readOnly: true }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCopy(generatedLinkDialog.dbCode, 'Register code')} disabled={!generatedLinkDialog.dbCode}>
            Copy Code
          </Button>
          <Button onClick={() => handleCopy(generatedLinkDialog.inviteUrl, 'Invite link')}>
            Copy Link
          </Button>
          <Button
            variant="contained"
            onClick={() => setGeneratedLinkDialog({ open: false, inviteUrl: '', dbCode: '' })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={expiryDialog.open}
        onClose={expiryDialogBusy ? undefined : handleCloseExpiryDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="trial-expiry-dialog-title"
      >
        <DialogTitle id="trial-expiry-dialog-title">
          {expiryDialog.mode === 'send'
            ? 'Send Trial-Expiry Email?'
            : 'Manage Final Trial Date'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                <strong>{expiryDialog.invite?.company_name || expiryDialog.invite?.email || 'This trial'}</strong>
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 14, color: 'text.secondary' }}>
                Register: <strong>{expiryDialog.invite?.database_name || 'N/A'}</strong>
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 14, color: 'text.secondary' }}>
                Recipient: <strong>{expiryRecipient}</strong>
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 14, color: 'text.secondary' }}>
                Automatic reminders:{' '}
                <strong>
                  {expiryDialog.invite?.lifecycle_notifications_enabled ? 'Enabled' : 'Disabled'}
                </strong>
              </Typography>
            </Box>

            {expiryDialog.error && (
              <Alert severity="error" role="alert">
                <AlertTitle>Action failed</AlertTitle>
                {expiryDialog.error}
              </Alert>
            )}

            {expiryDialog.result && (
              <Alert severity={expiryDialog.result.severity} role="status">
                <AlertTitle>{expiryDialog.result.title}</AlertTitle>
                {expiryDialog.result.message}
                {expiryDialog.result.recipientEmail && (
                  <Typography component="div" sx={{ mt: 1, fontSize: 14 }}>
                    Recipient: <strong>{expiryDialog.result.recipientEmail}</strong>
                  </Typography>
                )}
                {expiryDialog.result.expiryDate && (
                  <Typography component="div" sx={{ mt: 0.5, fontSize: 14 }}>
                    Inclusive final day:{' '}
                    <strong>{dayjs(expiryDialog.result.expiryDate).format('DD MMM YYYY')}</strong>
                  </Typography>
                )}
                {expiryDialog.result.deliveryStatus && (
                  <Typography component="div" sx={{ mt: 0.5, fontSize: 14 }}>
                    Delivery status: <strong>{String(expiryDialog.result.deliveryStatus).toUpperCase()}</strong>
                  </Typography>
                )}
                {expiryDialog.result.reactivated && (
                  <Typography component="div" sx={{ mt: 0.5, fontSize: 14 }}>
                    The trial was reactivated through the new final day.
                  </Typography>
                )}
                {expiryDialog.result.requiresManualReconciliation && (
                  <Typography component="div" sx={{ mt: 0.5, fontSize: 14, fontWeight: 600 }}>
                    Check the email provider logs before attempting another manual send.
                  </Typography>
                )}
              </Alert>
            )}

            {expiryDialog.mode === 'edit' ? (
              <>
                <TextField
                  label="Final trial day (inclusive)"
                  type="date"
                  value={expiryDialog.expiryDate}
                  onChange={(event) => setExpiryDialog((previous) => ({
                    ...previous,
                    expiryDate: event.target.value,
                    error: '',
                    result: null,
                  }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'aria-label': 'Final trial day, inclusive' }}
                  helperText={expiryDateValid
                    ? 'The customer keeps access through this entire calendar day.'
                    : 'Enter a real calendar date in YYYY-MM-DD format.'}
                  error={Boolean(expiryDialog.expiryDate) && !expiryDateValid}
                  disabled={expiryDialogBusy || Boolean(expiryDialog.result)}
                  fullWidth
                  required
                />
                <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
                  {[7, 14, 30].map(days => <Button key={days} size="small" variant="outlined" disabled={expiryDialogBusy || Boolean(expiryDialog.result) || !isValidDateOnly(expiryDialog.invite?.expiry_date)} onClick={() => setExpiryDialog(previous => ({ ...previous, expiryDate: dayjs(previous.invite.expiry_date).add(days, 'day').format('YYYY-MM-DD'), error: '', result: null }))}>+{days} days from current final day</Button>)}
                </Stack>
                <Alert severity="info">
                  This date is inclusive: access continues through the selected final day and expires afterward.
                  Saving a future date for an expired trial reactivates it through that day.
                </Alert>
                <Alert severity="info">
                  <strong>Save Date &amp; Send Email</strong> saves first, then immediately sends the reminder
                  template for a current or future final day. A past final day uses the trial-ended notice.
                  This manual email does not require automatic reminders to be enabled.
                </Alert>
                {!expiryDialogAvailability.canSend && expiryDialogAvailability.sendReason && (
                  <Alert severity="warning">
                    You can still save the final date, but sending is unavailable: {expiryDialogAvailability.sendReason}
                  </Alert>
                )}
              </>
            ) : (
              <>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: 'rgba(15,17,21,0.035)',
                    border: '1px solid rgba(15,17,21,0.08)',
                  }}
                >
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                    Inclusive final day:{' '}
                    <strong>
                      {expiryDialog.expiryDate
                        ? dayjs(expiryDialog.expiryDate).format('DD MMM YYYY')
                        : 'N/A'}
                    </strong>
                  </Typography>
                </Box>
                <Alert severity="warning">
                  Confirm this recipient and final date before sending. EXREG sends a reminder when the final day
                  is current or upcoming, or a trial-ended notice when it has passed.
                </Alert>
                <Alert severity="info">
                  This is a manual one-time action. It works even when automatic trial reminders are disabled and
                  does not change that preference.
                </Alert>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          {expiryDialog.result ? (
            <Button variant="contained" onClick={handleCloseExpiryDialog}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseExpiryDialog} disabled={expiryDialogBusy}>
                Cancel
              </Button>
              {expiryDialog.mode === 'edit' ? (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => handleExpiryDialogSubmit({ saveDate: true, sendEmail: false })}
                    disabled={
                      expiryDialogBusy
                      || !expiryDialogAvailability.canEdit
                      || !expiryDateValid
                      || !expiryDateChanged
                    }
                  >
                    {expiryDialog.submitting === 'save' ? 'Saving…' : 'Save Date'}
                  </Button>
                  <Tooltip
                    title={expiryDialogAvailability.canSend
                      ? 'Save the inclusive final date, then send the appropriate expiry email'
                      : expiryDialogAvailability.sendReason || ''}
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<MailOutlineIcon />}
                        onClick={() => handleExpiryDialogSubmit({ saveDate: true, sendEmail: true })}
                        disabled={
                          expiryDialogBusy
                          || !expiryDialogAvailability.canEdit
                          || !expiryDialogAvailability.canSend
                          || !expiryDateValid
                          || !expiryDateChanged
                        }
                      >
                        {expiryDialog.submitting === 'save-and-send'
                          ? 'Saving & Sending…'
                          : 'Save Date & Send Email'}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<MailOutlineIcon />}
                  onClick={() => handleExpiryDialogSubmit({ saveDate: false, sendEmail: true })}
                  disabled={expiryDialogBusy || !expiryDialogAvailability.canSend}
                >
                  {expiryDialog.submitting === 'send' ? 'Sending…' : 'Send Expiry Email'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={actionBusyId === deleteDialog.invite?.id ? undefined : handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Trial Invite</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14, color: 'rgba(15,17,21,0.72)' }}>
            Delete invite for <strong>{deleteDialog.invite?.company_name || deleteDialog.invite?.email || 'this trial'}</strong>.
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 14, color: 'rgba(15,17,21,0.62)' }}>
            Register: <strong>{deleteDialog.invite?.database_name || 'N/A'}</strong>
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Deleting the invite stops future trial-expiry emails. The workspace and database are retained unless
            you select the database deletion option below.
          </Alert>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={deleteDialog.deleteDatabase}
                disabled={actionBusyId === deleteDialog.invite?.id}
                onChange={(event) =>
                  setDeleteDialog((prev) => ({
                    ...prev,
                    deleteDatabase: event.target.checked,
                    confirmationDatabaseName: '',
                  }))
                }
              />
            }
            label="Also delete the trial database"
          />
          {deletionError && <Alert severity="error" sx={{ mt: 1 }}>{deletionError}</Alert>}
          {deleteDialog.deleteDatabase && <RegisterDeletionPreview state={deletionPreview} />}
          {deleteDialog.deleteDatabase && (
            <TextField
              disabled={actionBusyId === deleteDialog.invite?.id}
              label="Type database name to confirm"
              value={deleteDialog.confirmationDatabaseName}
              onChange={(event) =>
                setDeleteDialog((prev) => ({
                  ...prev,
                  confirmationDatabaseName: event.target.value,
                }))
              }
              helperText={`Required: ${deleteDialog.invite?.database_name || ''}`}
              fullWidth
              margin="normal"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={actionBusyId === deleteDialog.invite?.id}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteInvite}
            disabled={
              actionBusyId === deleteDialog.invite?.id ||
              (deleteDialog.deleteDatabase &&
                (!deletionPreview.preview || deleteDialog.confirmationDatabaseName.trim() !== deleteDialog.invite?.database_name))
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={conversionDialog.open}
        onClose={actionBusyId === conversionDialog.invite?.id ? undefined : handleCloseConversionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Trial as Converted?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14, color: 'rgba(15,17,21,0.72)' }}>
            Mark <strong>{conversionDialog.invite?.company_name || conversionDialog.invite?.email || 'this trial'}</strong> as
            a converted customer.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action only records the conversion and stops future trial-expiry emails. It does not extend
            customer access or update the expiry date, plan, or contract.
          </Alert>
          <Typography sx={{ mt: 1.5, fontSize: 14, color: 'rgba(15,17,21,0.62)' }}>
            Update the customer&apos;s expiry date and contract separately. The workspace and database
            <strong> {conversionDialog.invite?.database_name || ''}</strong> will remain unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseConversionDialog}
            disabled={actionBusyId === conversionDialog.invite?.id}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={handleConvertTrial}
            disabled={actionBusyId === conversionDialog.invite?.id}
          >
            Mark Converted &amp; Stop Emails
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={notificationDialog.open}
        onClose={
          actionBusyId === notificationDialog.invite?.id ? undefined : handleCloseNotificationDialog
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {notificationDialog.enabled
            ? notificationDialog.invite?.lifecycle_requires_review
              ? 'Review and Enable Trial Emails?'
              : 'Enable Trial-Expiry Emails?'
            : 'Disable Trial-Expiry Emails?'}
        </DialogTitle>
        <DialogContent dividers>
          {notificationDialog.enabled && notificationDialog.invite?.lifecycle_requires_review && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This historical trial was imported with emails off. Verify the recipient and trial end date before
              enabling notifications.
            </Alert>
          )}
          {notificationDialog.enabled && (
            <TextField
              label="Notification recipient"
              type="email"
              value={notificationDialog.email}
              onChange={(event) => setNotificationDialog((previous) => ({
                ...previous,
                email: event.target.value,
              }))}
              helperText="Expiry warnings and the expired notice will be sent to this address."
              fullWidth
              margin="normal"
              required
            />
          )}
          <Typography sx={{ fontSize: 14, color: 'rgba(15,17,21,0.72)' }}>
            {notificationDialog.enabled ? 'Enable' : 'Disable'} automated trial-expiry emails for{' '}
            <strong>
              {notificationDialog.invite?.company_name ||
                notificationDialog.invite?.email ||
                'this trial'}
            </strong>
            .
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(15,17,21,0.035)',
              border: '1px solid rgba(15,17,21,0.08)',
            }}
          >
            <Typography sx={{ fontSize: 14, color: 'rgba(15,17,21,0.68)' }}>
              Recipient:{' '}
              <strong>
                {notificationDialog.enabled
                  ? String(notificationDialog.email || '').trim().toLowerCase() || 'N/A'
                  : notificationDialog.invite?.lifecycle_notification_email || 'N/A'}
              </strong>
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 14, color: 'rgba(15,17,21,0.68)' }}>
              Trial ends:{' '}
              <strong>
                {notificationDialog.invite?.expiry_date
                  ? dayjs(notificationDialog.invite.expiry_date).format('DD MMM YYYY')
                  : 'N/A'}
              </strong>
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            This changes trial-expiry emails only. It does not extend or restrict access, change the trial end
            date, or update the contract. Make those changes separately.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseNotificationDialog}
            disabled={actionBusyId === notificationDialog.invite?.id}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={notificationDialog.enabled ? 'primary' : 'warning'}
            startIcon={
              notificationDialog.enabled ? <NotificationsActiveIcon /> : <NotificationsOffIcon />
            }
            onClick={handleUpdateTrialNotifications}
            disabled={actionBusyId === notificationDialog.invite?.id}
          >
            {notificationDialog.enabled ? 'Enable Emails' : 'Disable Emails'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
